// src/utils/certificados.js
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore"
import { db } from "@/firebase"
import { Timestamp } from "firebase/firestore"
import { nanoid } from "nanoid" // más corto y legible para QR
import { getFunctions, httpsCallable } from "firebase/functions"

/**
 * Crea un nuevo certificado de regalo
 */
export async function crearCertificado({
  ventaId,
  tipo = "monto",              // 'monto' | 'producto' | 'servicio'
  descripcion = "",
  valor = 0,
  destinatario = {},
  expiracionDias = 90
}) {
  const id = nanoid(10)

  const data = {
    id,
    ventaId,
    tipo,
    descripcion,
    valor,
    redimido: false,
    creado: Timestamp.now(),
    expiracion: Timestamp.fromDate(new Date(Date.now() + expiracionDias * 86400000)),
    destinatario: {
      nombre: destinatario.nombre || "",
      email: destinatario.email || ""
    }
  }

  await setDoc(doc(db, "gift_certificates", id), data)

  return data
}

/**
 * Obtiene un certificado por su ID (por ejemplo, desde QR)
 */
export async function obtenerCertificado(id) {
  const snap = await getDoc(doc(db, "gift_certificates", id))
  if (!snap.exists()) return null
  return snap.data()
}

/**
 * Marca el certificado como redimido (si está activo)
 */
export async function redimirCertificado(id, redimidoPor = null) {
  const ref = doc(db, "gift_certificates", id)
  const snap = await getDoc(ref)

  if (!snap.exists()) throw new Error("Certificado no encontrado")

  const cert = snap.data()

  if (cert.redimido) throw new Error("Este certificado ya fue redimido")
  
  const now = new Date()
  const vencimiento = cert.expiracion?.toDate?.() || new Date(0)
  if (now > vencimiento) throw new Error("Este certificado ha expirado")

  await updateDoc(ref, {
    redimido: true,
    redimidoEn: Timestamp.now(),
    redimidoPor: redimidoPor || null
  })

  return true
}

/**
 * Envía el certificado por correo al destinatario
 */
export async function enviarCorreoCertificado(certificado, pdfBase64) {
  const functions = getFunctions(undefined, "us-central1") // asegúrate de usar la misma región que tus CFs
  const sendEmail = httpsCallable(functions, "enviarCorreoGenerico")

  const subject = "Certificado de regalo - Nat Spa"
  const remitente = certificado.remitenteNombre || "NAT Spa"
  const monto = Number(certificado.monto ?? certificado.valor ?? 0).toFixed(2)
  const html = `
    <div style="font-family: sans-serif; color: #0f172a; padding: 1rem;">
      <h2 style="margin-bottom: 0.5rem;">¡${remitente} te envió un regalo!</h2>
      <p>Has recibido un certificado de regalo para usar en Nat Spa.</p>
      <p style="margin: 0.5rem 0;"><strong>Valor:</strong> $${monto}</p>
      <p style="margin: 0;">Mensaje del remitente:</p>
      <blockquote style="margin: 0.5rem 0; padding-left: 1rem; border-left: 3px solid #94a3b8;">
        ${certificado.mensaje || "¡Disfruta tu experiencia en Nat Spa!"}
      </blockquote>
      <p style="margin-top: 1rem;">Presenta el certificado adjunto para redimirlo.</p>
    </div>
  `

  await sendEmail({
    to: certificado.destinatarioEmail,
    subject,
    html,
    pdfBase64,
    fileName: `certificado_${certificado.uuid}.pdf`,
  })
}

/**
 * Verifica si un certificado es válido (existe, activo, no expirado, no redimido)
 */
export async function verificarCertificado(id) {
  const ref = doc(db, "gift_certificates", id);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    throw new Error("Certificado no encontrado");
  }

  const cert = snap.data();

  if (cert.redimido) {
    throw new Error("Este certificado ya fue redimido");
  }

  const vencimiento = cert.expiracion?.toDate?.() || new Date(0);
  const ahora = new Date();

  if (ahora > vencimiento) {
    throw new Error("Este certificado ha expirado");
  }

  return { ...cert, id };
}
